import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, PencilSimple, Buildings, Users } from "@phosphor-icons/react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { StatusBadge, fmtDate } from "./ui";
import { AccountsVault } from "@/components/AccountsVault";
import { Goals } from "@/components/Goals";

const TIERS = ["Tier 1", "Tier 2", "Tier 3"];

export default function CompanyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [accounts, setAccounts] = useState([]);

  const load = useCallback(async () => {
    try {
      const [c, a] = await Promise.all([api.get(`/cc/companies/${id}`), api.get("/cc/accounts", { params: { company_id: id } })]);
      setCompany(c.data.company || c.data); setAccounts(a.data);
    } catch { setCompany(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (company === null) return <div className="flex items-center justify-center py-20"><div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>;
  if (company === false) return <div className="text-muted-foreground">Company not found. <button onClick={() => navigate("/command/companies")} className="text-primary underline">Back</button></div>;

  const owners = company.owners || [];
  const tierDone = (t) => accounts.some((a) => a.tier === t);

  return (
    <div className="space-y-8" data-testid="company-detail-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <button onClick={() => navigate("/command/companies")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-2"><ArrowLeft size={16} /> Companies</button>
          <div className="flex items-center gap-3">
            <Buildings size={26} className="text-primary" />
            <h1 className="font-serif text-3xl">{company.legal_name}</h1>
            <StatusBadge status={company.status} />
          </div>
          <div className="text-sm text-muted-foreground mt-1">{[company.entity_type, company.city, company.state].filter(Boolean).join(" · ")}</div>
        </div>
        <Button variant="outline" size="sm" onClick={() => navigate(`/command/companies?edit=${id}`)} data-testid="edit-company-detail-button" className="gap-2 border-white/15"><PencilSimple size={16} /> Edit details</Button>
      </div>

      <section className="rounded-xl border border-white/10 bg-zinc-900/50 p-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm" data-testid="company-info">
        {[["DBA", company.dba], ["EIN", company.ein], ["NAICS", company.naics_code], ["Formation date", company.formation_date && fmtDate(company.formation_date)], ["Address", company.address], ["City / State / Zip", [company.city, company.state, company.zip_code].filter(Boolean).join(", ")], ["Phone", company.phone], ["Email", company.email], ["Website", company.website], ["Industry", company.industry], ["Registered agent", company.registered_agent]].map(([k, v]) => (
          <div key={k}><div className="text-xs uppercase tracking-wider text-muted-foreground">{k}</div><div className="mt-0.5">{v || "—"}</div></div>
        ))}
      </section>

      <section data-testid="company-owners">
        <div className="flex items-center gap-2 mb-3"><Users size={20} className="text-primary" /><h2 className="text-lg font-medium">Business owners ({owners.length})</h2></div>
        {owners.length === 0 ? (
          <div className="rounded-xl border border-dashed border-white/15 p-6 text-center text-sm text-muted-foreground">No additional owners. Add them via "Edit details".</div>
        ) : (
          <div className="rounded-xl border border-white/10 bg-zinc-900/50 divide-y divide-white/5">
            {owners.map((o, i) => (
              <div key={i} className="flex items-center justify-between px-4 py-3 text-sm">
                <div><span className="font-medium">{o.name}</span>{o.title ? <span className="text-muted-foreground"> · {o.title}</span> : ""}</div>
                <div className="text-xs text-muted-foreground">{o.ownership_pct ? `${o.ownership_pct}%` : ""}{o.email ? ` · ${o.email}` : ""}{o.phone ? ` · ${o.phone}` : ""}</div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section data-testid="company-tiers">
        <h2 className="text-lg font-medium mb-3">Vendor credit tiers</h2>
        <div className="grid grid-cols-3 gap-3">
          {TIERS.map((t) => {
            const count = accounts.filter((a) => a.tier === t).length;
            return (
              <div key={t} className={`rounded-xl border p-4 text-center ${tierDone(t) ? "border-emerald-500/30 bg-emerald-500/10" : "border-white/10 bg-zinc-900/50"}`} data-testid={`tier-${t.replace(" ", "-")}`}>
                <div className="font-medium">{t}</div>
                <div className={`text-2xl font-display tracking-tighter ${tierDone(t) ? "text-emerald-400" : "text-muted-foreground"}`}>{count}</div>
                <div className="text-xs text-muted-foreground">{count === 1 ? "account" : "accounts"}</div>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground mt-2">Assign a tier when you add a vendor account below.</p>
      </section>

      <section><AccountsVault companyId={id} showTier /></section>
      <section><Goals companyId={id} /></section>
    </div>
  );
}
